import {createSlice, createAsyncThunk, PayloadAction} from '@reduxjs/toolkit';
import {RequestStatus} from '../../utils/Enums';
import {apiClient} from '../../utils/ApiClient';
import {ClientContact} from "../../models/ClientContact";
import {VendorContact} from "../../models/VendorContact";

interface ContactsState {
    contacts: any[];
    pagination: any;
    language: string;
    type: string;
    getContactsStatus: RequestStatus;
    sendEmailStatus: RequestStatus;
    sendSMSStatus: RequestStatus;
}

const initialState: ContactsState = {
    contacts: [],
    pagination: null,
    language: 'est',
    type: 'vendors',
    getContactsStatus: RequestStatus.Idle,
    sendEmailStatus: RequestStatus.Idle,
    sendSMSStatus: RequestStatus.Idle
};

export const getContacts = createAsyncThunk(
    'contacts/getContacts',
    async ({type, language, categories, country, page}: { type: string; language: string; categories: number[]; country: string; page:number }) => {
        const response = await apiClient.post(`categories/get-contacts/${type}?country=${country}&page=${page}`, {
            language,
            categories
        });
        return response.data;
    }
);

export const sendEmail = createAsyncThunk(
    'contacts/sendEmail',
    async ({subject, content}: any, thunkAPI) => {
        const contactsState = thunkAPI.getState() as { contacts: ContactsState };
        const {type, contacts} = contactsState.contacts;
        const contactIds = contacts.map(contact => contact.id);

        const selectedCategoryIds = JSON.parse(sessionStorage.getItem('selectedIds') || '[]');

        const response = await apiClient.post(`send-email/${type}`, {
            contacts: contactIds,
            subject,
            content,
            categories: selectedCategoryIds
        });

        return response.data;
    }
);

export const sendEmailRecommended = createAsyncThunk(
    'contacts/sendEmailRecommended',
    async ({subjectEst, contentEst, subjectEng, contentEng}: any, thunkAPI) => {
        const contactsState = thunkAPI.getState() as { contacts: ContactsState };
        const {type, contacts} = contactsState.contacts;

        let contactIds = []

        const storedRecipients = sessionStorage.getItem('selectedRecommendations');
        if ( storedRecipients ) {
            const recipients = JSON.parse(storedRecipients);
            contactIds = recipients.map((recipient: VendorContact) => recipient.id);
        }

        const response = await apiClient.post(`send-email-recommended`, {
            contacts: contactIds,
            subjectEst,
            contentEst,
            subjectEng,
            contentEng
        });

        return response.data;
    }
);

export const sendSMS = createAsyncThunk(
    'contacts/sendSMS',
    async ({content, sender}: { content: string; sender: string }, thunkAPI) => {
        const contactsState = thunkAPI.getState() as { contacts: ContactsState }; // Type assertion
        const {type, contacts} = contactsState.contacts;
        const contactIds = contacts.map(contact => contact.id);
        const response = await apiClient.post(`send-sms/${type}`, {contacts: contactIds, content, sender});
        return response.data;
    }
);

const contactsSlice = createSlice({
    name: 'contacts',
    initialState,
    reducers: {
        reseGetContactsStatus: (state) => {
            console.log('AABB', 'Resetting');
            state.getContactsStatus = RequestStatus.Idle;
        },
        resetSendEmail: (state) => {
            state.sendEmailStatus = RequestStatus.Idle;
        },
        resetSendSMS: (state) => {
            state.sendSMSStatus = RequestStatus.Idle;
        },
        setLanguage: (state, action) => {
            state.language = action.payload;
        },
        setType: (state, action) => {
            state.type = action.payload;
        },
        setContacts: (state, action) => {
            state.contacts = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getContacts.pending, (state) => {
                state.getContactsStatus = RequestStatus.Loading;
            })
            .addCase(getContacts.fulfilled, (state, action) => {
                state.getContactsStatus = RequestStatus.Success;
                state.contacts = action.payload.data;
                state.pagination = action.payload.pagination;
            })
            .addCase(getContacts.rejected, (state) => {
                state.getContactsStatus = RequestStatus.Failed;
            })
            .addCase(sendEmail.pending, (state) => {
                state.sendEmailStatus = RequestStatus.Loading;
            })
            .addCase(sendEmail.fulfilled, (state) => {
                state.sendEmailStatus = RequestStatus.Success;
            })
            .addCase(sendEmail.rejected, (state) => {
                state.sendEmailStatus = RequestStatus.Failed;
            })
            .addCase(sendSMS.pending, (state) => {
                state.sendSMSStatus = RequestStatus.Loading;
            })
            .addCase(sendSMS.fulfilled, (state) => {
                state.sendSMSStatus = RequestStatus.Success;
            })
            .addCase(sendSMS.rejected, (state) => {
                state.sendSMSStatus = RequestStatus.Failed;
            })
            .addCase(sendEmailRecommended.pending, (state) => {
                state.sendEmailStatus = RequestStatus.Loading;
            })
            .addCase(sendEmailRecommended.fulfilled, (state) => {
                state.sendEmailStatus = RequestStatus.Success;
            })
            .addCase(sendEmailRecommended.rejected, (state) => {
                state.sendEmailStatus = RequestStatus.Failed;
            });
    },
});

export const {
    reseGetContactsStatus,
    setLanguage,
    setType,
    setContacts,
    resetSendEmail,
    resetSendSMS
} = contactsSlice.actions;

export default contactsSlice.reducer;